import { z } from 'zod';
import { EncryptedArtifactRef } from './artifact-ref.js';
import { AutoArtifactFields } from './auto-artifacts.js';
import { CallFrame } from './call.js'; // viko-call
import { Agent, ContentPart, FileDiff, Id, ImageRef, Json, TimestampMs } from './common.js';
import { ProgressPayload } from './progress.js';
import { UsageLimitPark } from './usage-limit.js';
import { PROTOCOL_VERSION } from './version.js';
/**
 * Normalized transcript events.
 *
 * Every event shares the envelope below. Events are produced by the daemon (via
 * `@viko/normalize`) from the native CLI stream, stamped with a per-run `seq`, stored by
 * the hub and replayed to clients. See docs/protocol.md for the catalog and rules.
 */
export const EventEnvelope = z.object({
    v: z.literal(PROTOCOL_VERSION),
    runId: Id,
    /** Per-run, gap-free, monotonically increasing sequence number assigned by the daemon (starts at 1). */
    seq: z.number().int().positive(),
    /** Globally unique, stable id for this event (dedupe key alongside (runId, seq)). */
    eventId: Id,
    ts: TimestampMs,
    /** Turn this event belongs to. Absent for run-level events outside a turn (run.start, ratelimit, status, …). */
    turnId: Id.optional(),
    /**
     * Nesting: the `toolCallId` of the enclosing subagent tool call (Claude Task/Agent,
     * Codex spawnAgent). Absent for top-level events.
     */
    parentId: Id.optional(),
    agent: Agent,
});
const ev = (kind, shape) => EventEnvelope.extend({ kind: z.literal(kind), ...shape });
export const ToolKind = z.enum([
    'shell',
    'edit',
    'write',
    'read',
    'search',
    'web',
    'mcp',
    'subagent',
    'todo',
    'plan',
    'image_gen',
    'question',
    'other',
]);
export const BlockType = z.enum(['text', 'thinking']);
export const PermissionMode = z.string();
// ---------------------------------------------------------------------------------------------
// Run / turn lifecycle
// ---------------------------------------------------------------------------------------------
export const RunStartEvent = ev('run.start', {
    /** Native session id (Claude session_id / Codex thread id). */
    nativeSessionId: z.string().optional(),
    cwd: z.string().optional(),
    model: z.string().optional(),
    effort: z.string().optional(),
    /** additive (g18): why Viko picked this model (from `run.start`), shown in the run header. */
    modelReason: z.string().max(300).optional(),
    permissionMode: PermissionMode.optional(),
    cliVersion: z.string().optional(),
    tools: z.array(z.string()).optional(),
    mcpServers: z.array(z.object({ name: z.string(), status: z.string().optional() })).optional(),
    slashCommands: z.array(z.string()).optional(),
    /** True when this run resumed an existing native session. */
    resumed: z.boolean().optional(),
    /**
     * The CLI's selectable models as it reports them (Claude control `initialize`), in its order
     * ("Default (recommended)" first). Additive; absent for agents that do not report a list.
     */
    models: z
        .array(z.object({
        value: z.string().max(200),
        resolvedModel: z.string().max(200).optional(),
        displayName: z.string().max(200).optional(),
        description: z.string().max(500).optional(),
    }))
        .max(40)
        .optional(),
});
export const TurnStartEvent = ev('turn.start', {
    /** Native turn id where the agent has one (Codex). */
    nativeTurnId: z.string().optional(),
});
export const TurnOutcome = z.enum(['ok', 'error', 'interrupted']);
export const TurnEndEvent = ev('turn.end', {
    outcome: TurnOutcome,
    durationMs: z.number().nonnegative().optional(),
    /** Error text when outcome=error. */
    error: z.string().optional(),
    /** Native stop reason / subtype (e.g. end_turn, error_max_turns). */
    stopReason: z.string().optional(),
    /** Number of model round-trips in the turn (Claude num_turns). */
    modelCalls: z.number().int().nonnegative().optional(),
    /** Final answer text if the agent reports one (Claude result.result). */
    resultText: z.string().optional(),
});
export const RunStatus = z.enum(['running', 'idle', 'waiting', 'compacting', 'error']);
export const StatusEvent = ev('status', {
    state: RunStatus,
    detail: z.string().optional(),
});
// ---------------------------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------------------------
export const UserMsgEvent = ev('user.msg', {
    text: z.string(),
    content: z.array(ContentPart).optional(),
    /** Client/native message id (Claude uuid / Codex item id). */
    messageId: z.string().optional(),
    /** Queued while the agent was busy (delivered mid-turn / next turn). */
    queued: z.boolean().optional(),
    /**
     * Who produced it: `user` (typed / sent by a Viko client), `cli` (synthesized by the CLI,
     * e.g. slash-command expansion or hook prompt), `viko` (injected by Viko: feedback, answers).
     */
    origin: z.enum(['user', 'cli', 'viko']).optional(),
});
/**
 * Delivery lifecycle of a user message the host sent (Claude `command_lifecycle`):
 * queued while busy → started (consumed by a turn) → completed | cancelled.
 */
export const UserMsgStatusEvent = ev('user.msg.status', {
    messageId: Id,
    state: z.enum(['queued', 'started', 'completed', 'cancelled']),
});
/** Live-only streaming fragment. Superseded by the asst.block with the same blockId. */
export const AsstDeltaEvent = ev('asst.delta', {
    blockId: Id,
    blockType: BlockType,
    text: z.string(),
});
export const AsstBlockEvent = ev('asst.block', {
    blockId: Id,
    blockType: BlockType,
    text: z.string(),
    /** Native assistant message id the block belongs to. */
    messageId: z.string().optional(),
    model: z.string().optional(),
    /** Thinking content was redacted/encrypted by the provider. */
    redacted: z.boolean().optional(),
    /** Stream was cut short by an interrupt. */
    aborted: z.boolean().optional(),
    /** Codex agentMessage phase (commentary vs final answer) when known. */
    phase: z.string().optional(),
});
// ---------------------------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------------------------
export const ToolCallEvent = ev('tool.call', {
    toolCallId: Id,
    /** Native tool name (Bash, Edit, commandExecution, mcp__server__tool, …). */
    name: z.string(),
    toolKind: ToolKind,
    input: Json,
    /** One-line human title (command, path, query, description). */
    title: z.string().optional(),
    command: z.string().optional(),
    cwd: z.string().optional(),
    path: z.string().optional(),
    mcp: z.object({ server: z.string(), tool: z.string() }).optional(),
    /** Proposed diffs for edit/write kinds when known at call time. */
    diffs: z.array(FileDiff).optional(),
    /** For subagent calls: the subagent type and description. */
    subagent: z
        .object({ type: z.string().optional(), description: z.string().optional() })
        .optional(),
    background: z.boolean().optional(),
});
export const ToolOutputEvent = ev('tool.output', {
    toolCallId: Id,
    stream: z.enum(['stdout', 'stderr', 'combined', 'progress']),
    text: z.string(),
});
export const ToolResultStatus = z.enum(['ok', 'error', 'denied', 'interrupted']);
export const ToolResultEvent = ev('tool.result', {
    toolCallId: Id,
    status: ToolResultStatus,
    /** Plain-text rendering of the output (what the model saw, or aggregated output). */
    output: z.string().optional(),
    content: z.array(ContentPart).optional(),
    diffs: z.array(FileDiff).optional(),
    images: z.array(ImageRef).optional(),
    exitCode: z.number().int().optional(),
    durationMs: z.number().nonnegative().optional(),
    /** Tool-specific structured output (Claude tool_use_result / Codex item). */
    structured: Json.optional(),
});
// ---------------------------------------------------------------------------------------------
// Plans, todos, diffs
// ---------------------------------------------------------------------------------------------
export const PlanEvent = ev('plan', {
    planId: Id,
    /** Markdown plan text. */
    text: z.string(),
    toolCallId: z.string().optional(),
    filePath: z.string().optional(),
});
export const TodoStatus = z.enum(['pending', 'in_progress', 'completed']);
export const TodoEvent = ev('todo', {
    /** Full replacement list (latest wins). */
    items: z.array(z.object({
        id: z.string().optional(),
        text: z.string(),
        status: TodoStatus,
        activeForm: z.string().optional(),
    })),
    explanation: z.string().optional(),
    toolCallId: z.string().optional(),
});
/** Aggregate diff of the turn so far (latest wins per turn). */
export const DiffTurnEvent = ev('diff.turn', {
    files: z.array(FileDiff),
    unified: z.string().optional(),
});
// ---------------------------------------------------------------------------------------------
// Questions & approvals (pinned at L0; drive the Inbox)
// ---------------------------------------------------------------------------------------------
export const QuestionItem = z.object({
    id: z.string(),
    header: z.string().optional(),
    question: z.string(),
    options: z.array(z.object({
        label: z.string(),
        description: z.string().optional(),
        preview: z.string().optional(),
    })),
    multiSelect: z.boolean().default(false),
    /** Free-text "Other" answer allowed. */
    allowOther: z.boolean().default(true),
    secret: z.boolean().optional(),
});
export const QuestionEvent = ev('question', {
    questionId: Id,
    questions: z.array(QuestionItem).min(1),
    toolCallId: z.string().optional(),
    /** The agent is blocked until it is answered. */
    blocking: z.boolean(),
    /** Native request id to answer through (Claude control request id / Codex JSON-RPC id). */
    nativeRequestId: z.string().optional(),
    /** phase2-decisions: the decision entity with this ask's thread, options and evidence. */
    decisionId: z.string().optional(),
});
// ---- phase2-decisions: decision threads in the transcript (content stays in the sealed event) ----
/** A message in a question's decision thread (user, asking agent or triage). */
export const QuestionThreadEvent = ev('question.thread', {
    questionId: Id,
    decisionId: z.string().optional(),
    messageId: z.string(),
    author: z.enum(['user', 'agent', 'triage', 'system']),
    text: z.string().optional(),
    /** User messages: how it reached the agent. */
    delivery: z.enum(['delivered', 'queued', 'resumed', 'triage']).optional(),
    /** Attached files (names only; the files are sealed artifacts on the decision). */
    attachments: z
        .array(z.object({ name: z.string().optional(), mime: z.string().optional() }))
        .optional(),
});
/** The asking agent revised the options (the card shows "revised"). */
export const QuestionRevisedEvent = ev('question.revised', {
    questionId: Id,
    decisionId: z.string().optional(),
    optionsRevision: z.number().int().nonnegative(),
    /** The ask was withdrawn instead of revised. */
    withdrawn: z.boolean().optional(),
    /** The revised option set (diffed against the previous one in the viewer). */
    options: z.array(z.object({ label: z.string(), recommended: z.boolean().optional() })).optional(),
    /** Why the agent revised (its thread reply). */
    note: z.string().optional(),
});
// ---- end phase2-decisions ----
export const QuestionAnsweredEvent = ev('question.answered', {
    questionId: Id,
    /** Question id → selected labels and/or free text. */
    answers: z.record(z.string(), z.array(z.string())),
    notes: z.record(z.string(), z.string()).optional(),
    by: z.enum(['user', 'auto', 'timeout', 'cancelled']).optional(),
});
export const ApprovalKind = z.enum([
    'tool',
    'command',
    'file_change',
    'plan',
    'permissions',
    'elicitation',
]);
export const ApprovalEvent = ev('approval', {
    approvalId: Id,
    approvalKind: ApprovalKind,
    toolCallId: z.string().optional(),
    toolName: z.string().optional(),
    input: Json.optional(),
    title: z.string().optional(),
    command: z.string().optional(),
    cwd: z.string().optional(),
    reason: z.string().optional(),
    diffs: z.array(FileDiff).optional(),
    /** Plan markdown for plan approvals. */
    plan: z.string().optional(),
    /** Native "always allow" suggestions, opaque. */
    suggestions: Json.optional(),
    nativeRequestId: z.string().optional(),
});
export const ApprovalDecision = z.enum(['allow', 'allow_always', 'deny', 'cancel']);
export const ApprovalResolvedEvent = ev('approval.resolved', {
    approvalId: Id,
    decision: ApprovalDecision,
    message: z.string().optional(),
    updatedInput: Json.optional(),
    by: z.enum(['user', 'auto', 'policy', 'cli']).optional(),
});
// ---------------------------------------------------------------------------------------------
// Context, usage, limits
// ---------------------------------------------------------------------------------------------
export const CompactionEvent = ev('compaction', {
    trigger: z.enum(['manual', 'auto']),
    preTokens: z.number().int().nonnegative().optional(),
    postTokens: z.number().int().nonnegative().optional(),
    summary: z.string().optional(),
    durationMs: z.number().nonnegative().optional(),
});
export const TokenCounts = z.object({
    input: z.number().int().nonnegative().default(0),
    output: z.number().int().nonnegative().default(0),
    cacheRead: z.number().int().nonnegative().default(0),
    cacheWrite: z.number().int().nonnegative().default(0),
    reasoning: z.number().int().nonnegative().optional(),
    total: z.number().int().nonnegative().optional(),
});
export const UsageEvent = ev('usage', {
    /** Tokens for the last turn (or last model call when `scope` = `call`). */
    turn: TokenCounts.optional(),
    /** Running totals for the whole native session. */
    cumulative: TokenCounts.optional(),
    /** Cumulative estimated cost in USD (Claude). */
    costUsd: z.number().nonnegative().optional(),
    /** Tokens currently occupying the context window. */
    contextTokens: z.number().int().nonnegative().optional(),
    contextWindow: z.number().int().positive().optional(),
    /** 0–100. */
    contextPct: z.number().min(0).max(100).optional(),
    model: z.string().optional(),
    byModel: z
        .record(z.string(), z.object({
        tokens: TokenCounts,
        costUsd: z.number().nonnegative().optional(),
        contextWindow: z.number().int().positive().optional(),
    }))
        .optional(),
});
export const RateLimitWindowName = z.enum(['5h', '7d', '7d_opus', '7d_sonnet', 'overage', 'other']);
export const CreditBalance = z.object({
    hasCredits: z.boolean(),
    unlimited: z.boolean(),
    balance: z.string().max(100).nullable(),
    /** Absent when the provider does not report expiration. Never infer it. */
    expiresAt: TimestampMs.optional(),
});
export const RateLimitEvent = ev('ratelimit', {
    accountId: z.string().min(1).max(160).optional(),
    credits: CreditBalance.optional(),
    windows: z.array(z.object({
        window: RateLimitWindowName,
        /** Native identifier (five_hour, primary, …). */
        nativeName: z.string().optional(),
        /** 0–100 when known. */
        usedPct: z.number().min(0).optional(),
        resetsAt: TimestampMs.optional(),
        status: z.enum(['allowed', 'warning', 'rejected']).optional(),
        windowMinutes: z.number().int().positive().optional(),
    })),
    plan: z.string().optional(),
    /**
     * The provider refused work because the account is out of quota (e.g. Codex
     * `usageLimitExceeded`: "try again at Oct 3rd, 2026 6:05 PM"). `resetsAt` only when the
     * provider stated it; never inferred.
     */
    limitReached: z
        .object({
        resetsAt: TimestampMs.optional(),
        code: z.string().max(80).optional(),
    })
        .optional(),
});
// ---------------------------------------------------------------------------------------------
// Markers & misc
// ---------------------------------------------------------------------------------------------
export const MarkerType = z.enum([
    'resumed',
    'reconnected',
    'model_change',
    'mode_change',
    'continued_from',
    'interrupted',
    'conversation_reset',
    'review_mode',
    'vcs_change',
    'goal',
    /** The agent named the session (Codex thread name). `text` is the title. */
    'title',
]);
export const MarkerEvent = ev('marker', {
    marker: MarkerType,
    text: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    /** Referenced run / session (continued-from) when applicable. */
    ref: z.string().optional(),
});
export const ArtifactEvent = ev('artifact', {
    encrypted: EncryptedArtifactRef.optional(),
    thumbEncrypted: EncryptedArtifactRef.optional(),
    artifactId: Id,
    name: z.string(),
    mime: z.string().optional(),
    path: z.string().optional(),
    url: z.string().optional(),
    bytes: z.number().int().nonnegative().optional(),
    sha256: z.string().optional(),
    thumbUrl: z.string().optional(),
    toolCallId: z.string().optional(),
    caption: z.string().optional(),
    /** Additive: artifact id of a JPEG thumbnail (ArtifactWatcher). */
    thumbArtifactId: z.string().optional(),
    // ---- auto-artifacts: source, gallery/comparison collection, claude.ai artifact ----
    ...AutoArtifactFields,
    // ---- end auto-artifacts ----
});
export const HookEvent = ev('hook', {
    hookId: Id,
    hookName: z.string().optional(),
    hookEvent: z.string(),
    phase: z.enum(['started', 'progress', 'completed']),
    outcome: z.enum(['success', 'error', 'cancelled', 'blocked']).optional(),
    stdout: z.string().optional(),
    stderr: z.string().optional(),
    output: z.string().optional(),
    exitCode: z.number().int().optional(),
    toolCallId: z.string().optional(),
});
/** CLI-managed task lifecycle: background shells, subagents (fg/bg), workflows, MCP tasks. */
export const CliTaskEvent = ev('cli.task', {
    taskId: Id,
    taskType: z.enum(['shell', 'agent', 'workflow', 'mcp', 'other']),
    state: z.enum(['started', 'running', 'completed', 'failed', 'stopped']),
    toolCallId: z.string().optional(),
    description: z.string().optional(),
    background: z.boolean().optional(),
    summary: z.string().optional(),
    outputFile: z.string().optional(),
    lastTool: z.string().optional(),
    usage: z
        .object({
        totalTokens: z.number().int().nonnegative().optional(),
        toolUses: z.number().int().nonnegative().optional(),
        durationMs: z.number().nonnegative().optional(),
    })
        .optional(),
});
export const NoticeEvent = ev('notice', {
    level: z.enum(['info', 'warning']),
    text: z.string(),
    /** Native source (informational, warning, permission_denied, …). */
    code: z.string().optional(),
    /**
     * Additive: a command the viewer can offer to sign (e.g. `rotation_pending` → `run.start` of
     * the next session). Never executed without the user's signature.
     */
    action: z.object({ kind: z.string(), payload: z.unknown() }).optional(),
    /** Additive (G14): usage-limit park state for `usage-limit:*` notices. */
    usageLimit: UsageLimitPark.optional(),
});
export const ErrorEvent = ev('error', {
    message: z.string(),
    code: z.string().optional(),
    /** The CLI will retry automatically. */
    retrying: z.boolean().optional(),
    /** The run cannot continue. */
    fatal: z.boolean().optional(),
    detail: Json.optional(),
});
/**
 * Additive v0: ground-truth progress evidence from the daemon's probe (git state, classified
 * check runs with exit codes, landing readiness). Never produced by a normalizer. See
 * ./progress.ts and docs/protocol.md → Progress.
 */
export const ProgressEvent = ev('progress', ProgressPayload);
/** Explicit passthrough for native messages with no normalized mapping (yet). */
export const RawEvent = ev('raw', {
    /** Native type discriminator, e.g. `system/foo` or JSON-RPC method. */
    source: z.string(),
    data: Json,
});
/**
 * viko-call: one daemon → device frame of a Viko Call (captions, spoken chunks, `call.show`,
 * queue, secure batch). Only in a call's own stream; sealed like every event.
 */
export const CallFrameEvent = ev('call.frame', { frame: CallFrame });
export const TranscriptEvent = z.discriminatedUnion('kind', [
    RunStartEvent,
    TurnStartEvent,
    TurnEndEvent,
    StatusEvent,
    UserMsgEvent,
    UserMsgStatusEvent,
    AsstDeltaEvent,
    AsstBlockEvent,
    ToolCallEvent,
    ToolOutputEvent,
    ToolResultEvent,
    PlanEvent,
    TodoEvent,
    DiffTurnEvent,
    QuestionEvent,
    QuestionAnsweredEvent,
    QuestionThreadEvent, // phase2-decisions
    QuestionRevisedEvent, // phase2-decisions
    ApprovalEvent,
    ApprovalResolvedEvent,
    CompactionEvent,
    UsageEvent,
    RateLimitEvent,
    MarkerEvent,
    ArtifactEvent,
    HookEvent,
    CliTaskEvent,
    NoticeEvent,
    ErrorEvent,
    RawEvent,
    ProgressEvent,
    CallFrameEvent, // viko-call
]);
export const EVENT_KINDS = TranscriptEvent.options.map((o) => o.shape.kind.value);
/** Kinds that are never persisted beyond the live window (superseded by a later event). */
export const LIVE_ONLY_KINDS = new Set(['asst.delta']);
/**
 * Forward-compatible parse: events with a kind this build doesn't know (added by a newer
 * producer under the additive-only rule) are returned as `unknownKind` rather than failing,
 * so older clients can render a generic row instead of breaking.
 */
export function parseEvent(data) {
    const r = TranscriptEvent.safeParse(data);
    if (r.success)
        return { ok: true, event: r.data };
    const env = EventEnvelope.extend({ kind: z.string() }).safeParse(data);
    if (env.success && !EVENT_KINDS.includes(env.data.kind)) {
        return { ok: false, unknownKind: env.data.kind, envelope: env.data, data };
    }
    return { ok: false, error: r.error };
}
//# sourceMappingURL=events.js.map